import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, FolderGit2, Loader2, AlertCircle } from "lucide-react";
import { apiInstance as api } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";

/**
 * Stealth-Agent project attribution — SELF-SERVE control.
 *
 * Renders ONLY for stealth desktop-agent users. The backend decides that:
 * GET /my-project returns { isAgentUser:false } for everyone else, and we render
 * null in that case. Regular app users never see this — they already pick a
 * project when starting a timer.
 *
 * Wording is deliberate: "Tracking this to" (not "track on this project"), because
 * background tracking is ALREADY running. This dropdown only decides where that
 * time is attributed — it does NOT start or stop tracking.
 *
 * Forward-only, mirroring the backend: switching affects time recorded from now on;
 * already-recorded time keeps its original project. The UI states this explicitly so
 * nobody expects a retroactive re-attribution.
 */

const BASE = "/api/v1/stealth-agent/my-project";

/**
 * `compact` renders it as a single header button (for PageShell's `actions` slot)
 * instead of the full banner. In that mode the panel is PORTALED to document.body:
 * PageShell's header band sets `overflow: hidden`, which would otherwise clip the
 * dropdown exactly the way the banner's own glow wrapper once did.
 */
export function StealthProjectSelector({ onChange, style, compact = false }) {
  const t = useDashTheme();

  const [state, setState] = useState({ loading: true, isAgentUser: false });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  const wrapRef = useRef(null);
  const triggerRef = useRef(null); // focus returns here on Escape / after selecting
  const panelRef = useRef(null);   // so scrolling INSIDE the list doesn't self-close
  const mountedRef = useRef(true);
  const [anchor, setAnchor] = useState(null); // viewport rect for the portaled panel
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Measure the trigger before paint so the portaled panel never renders in the
  // wrong spot for a frame. Right-aligned to the trigger, flipped above when the
  // viewport can't fit it below.
  useLayoutEffect(() => {
    if (!compact || !open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const PANEL_MAX = 320;
    const below = window.innerHeight - r.bottom;
    setAnchor({
      right: Math.max(8, window.innerWidth - r.right),
      top: below < PANEL_MAX + 16 ? null : r.bottom + 8,
      bottom: below < PANEL_MAX + 16 ? window.innerHeight - r.top + 8 : null,
      maxHeight: Math.min(PANEL_MAX, Math.max(below, r.top) - 24),
    });
  }, [compact, open]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(BASE);
      if (!mountedRef.current) return;
      setState({
        loading: false,
        // Backend applies BOTH gates (org main_admin toggle + stealth-agent user) and
        // returns enabled:false if either fails. Older responses without the field
        // fall back to isAgentUser so nothing regresses.
        enabled: data?.enabled !== undefined ? !!data.enabled : !!data?.isAgentUser,
        isAgentUser: !!data?.isAgentUser,
        projects: data?.projects || [],
        activeProject: data?.activeProject || null,
        activeProjectId: data?.activeProjectId || null,
      });
    } catch (e) {
      if (!mountedRef.current) return;
      // Fail quiet: this is an optional control. If it can't load, hide it rather
      // than showing a broken widget on the dashboard.
      setState({ loading: false, enabled: false, isAgentUser: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Dismiss handling. A dropdown should close on every way a user can "leave" it,
  // not just a plain left-click:
  //  - pointerdown covers mouse + touch + pen in one listener
  //  - capture phase so a child that calls stopPropagation() can't trap it open
  //  - Escape returns focus to the trigger (keyboard users lose their place otherwise)
  //  - scrolling the page would detach the panel from its anchor, so close instead
  //  - window blur (alt-tab, devtools, clicking an iframe) leaves no other way to close
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // In compact mode the panel is portaled outside wrapRef, so it must be
      // checked separately or clicking a row would count as an outside click.
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inWrap && !inPanel) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation(); // don't also close a parent drawer/modal
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    // Ignore the scroll that happens *inside* the panel's own list.
    const onScroll = (e) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onBlur = () => setOpen(false);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [open]);

  const select = useCallback(async (projectId) => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus(); // don't strand keyboard focus
    setSaving(true);
    setError(null);
    try {
      await api.put(BASE, { projectId });
      if (!mountedRef.current) return;
      await load();
      if (!mountedRef.current) return;
      setJustSaved(true);
      setTimeout(() => mountedRef.current && setJustSaved(false), 2200);
      if (onChange) onChange(projectId);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(
        e?.response?.data?.error || "Couldn't save. Your previous project is still active."
      );
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [load, onChange]);

  // The selection can point at a project the user no longer has access to (removed
  // from the team). Surface that instead of silently rendering "Not set".
  const staleSelection = useMemo(
    () => !!(state.activeProjectId && !state.activeProject),
    [state.activeProjectId, state.activeProject]
  );

  if (state.loading || !state.enabled) return null;

  const current = state.activeProject;
  const projects = state.projects || [];

  // Unattributed is a state worth showing, not a neutral default — an unset selector
  // silently sends time nowhere. Amber (not red) since it's a nudge, not an error.
  const unset = !current && !staleSelection;
  const accent = staleSelection
    ? t.status.negative
    : unset
    ? t.status.warning
    : t.accents[0];

  // Shared by both the banner and the compact header dropdown.
  const panelBody =
    projects.length === 0 ? (
      <div style={{ padding: `${t.s[3]}px`, fontSize: t.font.size.sm, color: t.c.textTertiary, lineHeight: 1.5 }}>
        You're not on any project yet.
        <br />
        Ask your admin to add you to one.
      </div>
    ) : (
      <>
        <div
          style={{
            padding: `${t.s[1]}px ${t.s[2]}px ${t.s[2]}px`,
            fontSize: t.font.size.xs,
            fontWeight: t.font.weight.semibold,
            color: t.c.textTertiary,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Track my activity to
        </div>
        {projects.map((p) => (
          <Row
            key={p.id}
            t={t}
            label={p.name}
            selected={String(state.activeProjectId) === String(p.id)}
            onClick={() => select(p.id)}
          />
        ))}
        <div style={{ height: 1, background: t.c.border, margin: `${t.s[2]}px ${t.s[1]}px` }} />
        <Row
          t={t}
          label="Don't link to a project"
          hint="Time is still tracked, just unattributed"
          muted
          selected={!state.activeProjectId}
          onClick={() => select(null)}
        />
      </>
    );

  // ── Compact: a single header button (PageShell `actions` slot) ──────────────
  if (compact) {
    const label = current ? current.name : staleSelection ? "Project unavailable" : "Set project";
    return (
      <div ref={wrapRef} style={{ position: "relative", display: "inline-flex", ...(style || {}) }}>
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          disabled={saving}
          title={
            current
              ? `Background tracking is recorded against "${current.name}". Click to change.`
              : "Your background tracking isn't linked to a project yet."
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: t.s[2],
            height: 38,
            padding: `0 ${t.s[3]}px`,
            borderRadius: t.r.sm,
            border: `1px solid ${open ? accent : unset || staleSelection ? `${accent}66` : t.c.border}`,
            boxShadow: open ? `0 0 0 3px ${accent}1F` : "none",
            background: unset || staleSelection ? `${accent}0F` : t.c.surface,
            color: current ? t.c.text : accent,
            fontSize: t.font.size.md,
            fontWeight: t.font.weight.medium,
            fontFamily: t.font.family,
            cursor: saving ? "wait" : "pointer",
            maxWidth: 240,
            transition: "border-color .15s, box-shadow .15s",
          }}
        >
          {saving ? (
            <Loader2 size={15} className="animate-spin" style={{ flexShrink: 0 }} />
          ) : justSaved ? (
            <Check size={15} style={{ flexShrink: 0, color: t.status.positive }} />
          ) : (
            <FolderGit2 size={15} style={{ flexShrink: 0, color: accent }} />
          )}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {label}
          </span>
          <ChevronDown
            size={15}
            style={{
              flexShrink: 0,
              opacity: 0.6,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform .15s",
            }}
          />
        </button>

        {/* Portaled: PageShell's header band clips overflow. */}
        {createPortal(
          <AnimatePresence>
            {open && anchor && (
              <motion.div
                ref={panelRef}
                role="listbox"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.14 }}
                style={{
                  position: "fixed",
                  ...(anchor.top != null ? { top: anchor.top } : { bottom: anchor.bottom }),
                  right: anchor.right,
                  zIndex: 1000,
                  width: 280,
                  maxHeight: anchor.maxHeight,
                  overflowY: "auto",
                  borderRadius: t.r.md,
                  border: `1px solid ${t.c.border}`,
                  background: t.c.surface,
                  boxShadow: t.shadow.lg,
                  padding: t.s[2],
                  fontFamily: t.font.family,
                }}
              >
                {panelBody}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: t.s[4],
        flexWrap: "wrap",
        padding: `${t.s[4]}px ${t.s[5]}px`,
        borderRadius: t.r.md,
        border: `1px solid ${t.c.border}`,
        background: t.c.surface,
        boxShadow: t.shadow.sm,
        // NOTE: no `overflow: hidden` here — the dropdown is absolutely positioned
        // inside this container and would be clipped by it. The glow gets its own
        // clipping wrapper below instead.
        ...(style || {}),
      }}
    >
      {/* Accent glow — same visual language as StatChip on this page. Wrapped in its
          own overflow-hidden, inset layer so the rounded corners clip the blur
          WITHOUT also clipping the dropdown panel. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: t.r.md,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            left: -20,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: `${accent}12`,
            filter: "blur(24px)",
          }}
        />
      </div>

      {/* Icon tile */}
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: t.r.sm + 2,
          background: `linear-gradient(135deg, ${accent}22, ${accent}0D)`,
          color: accent,
          boxShadow: `inset 0 0 0 1px ${accent}26`,
        }}
      >
        <FolderGit2 size={18} />
      </span>

      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: t.font.size.md,
            fontWeight: t.font.weight.semibold,
            color: t.c.text,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          Tracking to
          {/* Live pulse — makes it obvious tracking is already running and this only
              decides attribution. */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px",
              borderRadius: t.r.pill,
              background: t.status.positiveSoft,
              color: t.status.positive,
              fontSize: t.font.size.xs,
              fontWeight: t.font.weight.semibold,
              letterSpacing: "0.02em",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: t.status.positive,
                boxShadow: `0 0 0 3px ${t.status.positiveSoft}`,
              }}
            />
            LIVE
          </span>
        </span>
        <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, lineHeight: 1.35 }}>
          Your background activity is recorded against this project
        </span>
      </div>

      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: t.s[2],
          marginLeft: "auto",
          height: 38,
          padding: `0 ${t.s[3]}px`,
          borderRadius: t.r.sm,
          border: `1px solid ${open ? accent : t.c.border}`,
          boxShadow: open ? `0 0 0 3px ${accent}1F` : "none",
          background: t.c.bg,
          color: current ? t.c.text : accent,
          fontSize: t.font.size.base,
          fontWeight: t.font.weight.medium,
          fontFamily: t.font.family,
          cursor: saving ? "wait" : "pointer",
          maxWidth: 280,
          transition: "border-color .15s, box-shadow .15s",
        }}
      >
        {saving ? (
          <Loader2 size={14} className="animate-spin" style={{ flexShrink: 0 }} />
        ) : (
          <span
            style={{
              width: 7,
              height: 7,
              flexShrink: 0,
              borderRadius: "50%",
              background: accent,
            }}
          />
        )}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {current ? current.name : staleSelection ? "Project unavailable" : "Choose project"}
        </span>
        <ChevronDown
          size={15}
          style={{
            flexShrink: 0,
            opacity: 0.65,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s",
          }}
        />
      </button>

      <AnimatePresence>
        {justSaved && (
          <motion.span
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "4px 10px",
              borderRadius: t.r.pill,
              background: t.status.positiveSoft,
              color: t.status.positive,
              fontSize: t.font.size.sm,
              fontWeight: t.font.weight.medium,
              whiteSpace: "nowrap",
            }}
          >
            <Check size={13} /> Saved — applies from now on
          </motion.span>
        )}
      </AnimatePresence>

      {(error || staleSelection) && (
        <span
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 7,
            width: "100%",
            padding: `${t.s[2]}px ${t.s[3]}px`,
            borderRadius: t.r.sm,
            background: t.status.negativeSoft,
            color: t.status.negative,
            fontSize: t.font.size.sm,
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          {error ||
            "Your selected project is no longer available to you. Pick another one."}
        </span>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="listbox"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.14 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: t.s[5],
              zIndex: 60,
              width: 280,
              maxHeight: 320,
              overflowY: "auto",
              borderRadius: t.r.md,
              border: `1px solid ${t.c.border}`,
              background: t.c.surface,
              boxShadow: t.shadow.lg,
              padding: t.s[2],
            }}
          >
            {panelBody}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Row({ t, label, hint, selected, muted, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="option"
      aria-selected={!!selected}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: t.s[2],
        width: "100%",
        padding: `${t.s[2]}px ${t.s[2]}px`,
        borderRadius: t.r.sm,
        border: "none",
        background: selected ? `${t.accents[0]}14` : hover ? t.c.surfaceHover : "transparent",
        color: muted ? t.c.textSecondary : t.c.text,
        fontSize: t.font.size.base,
        fontFamily: t.font.family,
        fontWeight: selected ? t.font.weight.medium : t.font.weight.regular,
        textAlign: "left",
        cursor: "pointer",
        transition: "background .12s",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          flexShrink: 0,
        }}
      >
        {selected ? <Check size={15} style={{ color: t.accents[0] }} /> : null}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        {hint ? (
          <span
            style={{
              display: "block",
              fontSize: t.font.size.xs,
              color: t.c.textTertiary,
              marginTop: 1,
            }}
          >
            {hint}
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default StealthProjectSelector;
